import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useUniversities } from "@src/presentation/hooks/universities"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Chip,
    Input,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Spinner,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem
} from "@heroui/react"
import {
    Search,
    Plus,
    MoreVertical,
    Eye,
    Edit,
    Building2,
    XCircle
} from "lucide-react"

export default function AllUniversitiesPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")

    // Fetch universities from API
    const { data: universitiesData, isLoading, error } = useUniversities()

    // Filter universities based on search
    const filteredUniversities = universitiesData?.universities?.filter((university: any) =>
        searchQuery === "" ||
        university.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        university.code.toLowerCase().includes(searchQuery.toLowerCase())
    ) || []

    const handleViewUniversity = (universityId: string) => {
        navigate(`/universities/${universityId}`)
    }

    const handleEditUniversity = (universityId: string) => {
        navigate(`/universities/${universityId}/edit`)
    }

    const handleCreateUniversity = () => {
        navigate('/universities/new')
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-danger">
                <XCircle className="h-12 w-12 mb-2" />
                <p>Error loading universities: {error.message}</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="flex h-8 sm:h-10 w-8 sm:w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md">
                                <Building2 className="h-4 sm:h-5 w-4 sm:w-5 text-primary-foreground" />
                            </div>
                            <div>
                                <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    University Management
                                </h1>
                                <p className="text-xs text-default-500 font-medium hidden sm:block">
                                    Manage universities and their information
                                </p>
                            </div>
                        </div>
                        <Button
                            color="primary"
                            startContent={<Plus className="h-4 w-4" />}
                            onPress={handleCreateUniversity}
                            className="hover:scale-105 transition-transform font-semibold rounded w-full sm:w-auto"
                            size="sm"
                        >
                            <span className="hidden sm:inline">Add University</span>
                            <span className="sm:hidden">Add University</span>
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">

                {/* Search */}
                <Card className="border border-border rounded shadow-none bg-card hover:shadow-custom transition-shadow">
                    <CardBody className="p-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Input
                                placeholder="Search universities..."
                                value={searchQuery}
                                onValueChange={setSearchQuery}
                                startContent={<Search className="h-4 w-4 text-default-400" />}
                                className="flex-1"
                            />
                        </div>
                    </CardBody>
                </Card>

                {/* Universities Table */}
                <Card className="border border-border rounded shadow-none bg-card hover:shadow-custom transition-shadow">
                    <CardHeader className="border-b border-border">
                        <h2 className="text-lg font-semibold">All Universities ({filteredUniversities.length})</h2>
                    </CardHeader>
                    <CardBody className="p-0">
                        {isLoading ? (
                            <div className="flex items-center justify-center p-8">
                                <Spinner size="lg" />
                            </div>
                        ) : filteredUniversities.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-8 text-default-500">
                                <Building2 className="h-12 w-12 mb-2" />
                                <p>No universities found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table aria-label="Universities table" classNames={{
                                    th: 'rounded',
                                    wrapper: 'rounded',
                                }}>
                                    <TableHeader>
                                        <TableColumn>NAME</TableColumn>
                                        <TableColumn>CODE</TableColumn>
                                        <TableColumn className="hidden md:table-cell">EMAIL</TableColumn>
                                        <TableColumn>STATUS</TableColumn>
                                        <TableColumn className="hidden md:table-cell">CREATED</TableColumn>
                                        <TableColumn>ACTIONS</TableColumn>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredUniversities.map((university) => (
                                            <TableRow key={university.university_id} className="border-y border-border min-h-[64px]">
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                            <Building2 className="h-4 w-4 text-primary" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-foreground">{university.name}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <code className="text-xs bg-default-100 px-2 py-1 rounded">
                                                        {university.code}
                                                    </code>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <span className="text-sm text-default-600">-</span>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="sm"
                                                        color={university.is_active ? 'success' : 'danger'}
                                                        variant="flat"
                                                    >
                                                        {university.is_active ? 'Active' : 'Inactive'}
                                                    </Chip>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <span className="text-xs text-default-500">
                                                        {new Date(university.created_at).toLocaleDateString()}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <Dropdown classNames={{
                                                        content: 'rounded'
                                                    }}>
                                                        <DropdownTrigger>
                                                            <Button
                                                                isIconOnly
                                                                variant="light"
                                                                size="sm"
                                                                className="rounded"
                                                            >
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownTrigger>
                                                        <DropdownMenu aria-label="University actions">
                                                            <DropdownItem
                                                                className="rounded"
                                                                key="view"
                                                                startContent={<Eye className="h-4 w-4" />}
                                                                onPress={() => handleViewUniversity(university.university_id)}
                                                            >
                                                                View Details
                                                            </DropdownItem>
                                                            <DropdownItem
                                                                key="edit"
                                                                className="rounded"
                                                                startContent={<Edit className="h-4 w-4" />}
                                                                onPress={() => handleEditUniversity(university.university_id)}
                                                            >
                                                                Edit University
                                                            </DropdownItem>
                                                        </DropdownMenu>
                                                    </Dropdown>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </main>
        </div>
    )
}